import { debounce } from 'lodash-es'
import {
    APPSTATE,
    LAYOUTSTATE,
    ROOMSSTATE,
    SECURESTATE,
    SETTINGSSTATE,
    SIDEBAR,
} from '.'
import { Size } from '../../types/mfc'
import {
    getBrowserBottomInset,
    PINNED_ROOM_MIN_WIDTH_FOR_CHAT,
} from '../components/theme'
import { LayoutEvent, SettingsType, ViewMode } from '../constants/enums'
import { FLOATING_PREVIEW } from '../constants/floaters'
import { isScreenOrientationSupported } from '../utilities/browser'
import { getHeight, getWidth } from '../utilities/dimensions'
import { installIOSPWAScrollRecovery } from '../utilities/iosPWAScrollRecovery'
import { getModalState, getScreenState } from './getState'
import { closeFloatingPreview, repositionFloater } from './internal/modals'
import { resolveOrientationViewMode } from './internal/orientationViewMode'
import { setRoomViewMode } from './internal/rooms'
import {
    applyLockedVideoDivision,
    calculateSizes,
    checkPinnedRoomChat,
    checkPinnedRoomLimit,
    getFullScreenRoomCoords,
    getOrientationState,
    setScreenState,
} from './internal/screen'
import {
    updateFloatingRoom,
    updateRoomCalculations,
} from './modals/state.floater'
import { pubSubLayout } from './pubSub'

export const resolveFullWindow = (
    overrideSkip = false,
    isVertical = getScreenState().portrait,
) => {
    const { viewMode, restoreViewMode } = getModalState().floating_room
    const inRoomOrMessage =
        document.location.pathname.includes('/room/') ||
        document.location.pathname.includes('/message/')
    const { fullWindow: fullWindowSetting } =
        SETTINGSSTATE[SettingsType.DEFAULT]
    const isMobile = APPSTATE.app.isMobile
    // not for an age-gated visitor, whose room stays at the normal level (see
    // canShowZoomVideo in layouts/Portrait)
    const autoFullWindow =
        fullWindowSetting !== false &&
        isMobile &&
        !isVertical &&
        inRoomOrMessage &&
        !SECURESTATE.ageVerification.ageGateActive

    const next = resolveOrientationViewMode({
        viewMode,
        restoreViewMode,
        isVertical,
        isMobile,
        autoFullWindow,
    })
    if (next.viewMode !== viewMode) {
        // setRoomViewMode ends in setScreenOrientation
        setRoomViewMode(next.viewMode, false, {
            restoreViewMode: next.restoreViewMode,
        })
        return
    }
    if (next.restoreViewMode !== restoreViewMode) {
        updateFloatingRoom({ restoreViewMode: next.restoreViewMode }, false)
    }
    setScreenOrientation({ overrideSkip })
}

const lastCalcSizeState = {
    show: null,
    fullWindow: null,
    expandedNav: false,
    expandedSearchMobile: false,
    composeFocused: false,
    quietFocus: false,
}

const lastViewport = {
    width: 0,
    height: 0,
    bottomInset: 0,
}

type ScreenOrientationProps = {
    ratio?: number
    // overrideSize?: Size
    overrideSkip?: boolean
}

const offsetContentSize = (width, height) => {
    if (
        LAYOUTSTATE.portal.composeFocused &&
        APPSTATE.app.isMobile &&
        width >= height
    ) {
        return width - 100
    }

    return width
}

export const setScreenOrientation = ({
    // overrideSize,
    overrideSkip,
    ratio,
}: ScreenOrientationProps = {}) => {
    const height = getHeight()
    const width = offsetContentSize(getWidth(), height)
    const isVertical = height > width
    const {
        portrait,
        calculated,
        ratio: propsRatio,
        contentWidth: currentContentWidth,
    } = getScreenState()
    const newRatio = ratio ?? propsRatio

    const sizeOptions = { height, width, ratio: newRatio }
    let sizes = calculateSizes(sizeOptions)
    if (
        sizes.contentWidth === currentContentWidth &&
        isVertical === portrait &&
        calculated &&
        !overrideSkip
    )
        return

    if (checkPinnedRoomLimit(ROOMSSTATE.pinnedRooms.length, sizes.pinLimit))
        sizes = calculateSizes(sizeOptions)
    const {
        contentWidth,
        contentHeight,
        pinsWidth,
        pinsHeight,
        singlePin,
        pinLimit,
        roomWidth,
        roomHeight,
        showChat,
    } = sizes
    if (portrait !== isVertical || pinsWidth < PINNED_ROOM_MIN_WIDTH_FOR_CHAT)
        checkPinnedRoomChat(showChat)
    if (!isVertical) applyLockedVideoDivision(roomWidth)

    // const { width: roomWidth, height: roomHeight } = isVertical
    //     ? calculatePortraitSize(height, width)
    //     : calculateLandscapeSize(height, width)

    // console.log(
    //     '[setScreenOrientation] after',
    //     { isVertical, height, width },
    //     { roomWidth, roomHeight },
    // )
    const almostSquare = contentWidth <= height && contentWidth > contentHeight
    const floatingRoom = getModalState().floating_room
    const orientationType = getOrientationState({
        almostSquare,
        windowWidth: width,
        portrait: isVertical,
        expandedNav: LAYOUTSTATE.layout.expandedNav,
        fullWindow: floatingRoom.viewMode === ViewMode.FullWindow,
        sidebarOpen: SIDEBAR.state.show,
        isMobile: APPSTATE.app.isMobile,
        expanded: floatingRoom.expanded,
    })

    const { x: roomX, y: roomY } = getFullScreenRoomCoords({
        floaterPortrait: isVertical,
        windowWidth: width,
    })

    const pinnedCount = ROOMSSTATE.pinnedRooms.length
    const hasPinnedRooms = pinnedCount > 0
    const pinLimitReached = pinnedCount >= pinLimit

    setScreenState({
        portrait: isVertical,
        roomHeight,
        roomWidth,
        roomX,
        roomY,
        calculated: true,
        windowWidth: width,
        windowHeight: height,
        contentWidth,
        contentHeight,
        pinsWidth,
        pinsHeight,
        singlePin,
        pinLimit,
        ratio: newRatio,
        orientationType,
        hasPinnedRooms,
        pinLimitReached,
    })
    updateRoomCalculations()
}

const checkAndRepositionOffscreenFloaters = () => {
    const { floater: floaters } = getModalState()
    // 1) loop through the opened floaters
    Object.keys(floaters).forEach((modalKey: string) => {
        const floater = floaters[modalKey]

        if (floater.type === FLOATING_PREVIEW && !floater.show) {
            closeFloatingPreview(floater.id)
        }
        // don't reposition chat heads and whatever else can be minimized,
        // as `floaterMomentum` is responsible for that
        if (!floater.minimized) repositionFloater(floater)
    })
}

const debouncedFloaterCheck = debounce(checkAndRepositionOffscreenFloaters, 400)

// this is kind of a messy catch all for the pubSubs
interface UpdateFullscreenOptions {
    show?: boolean // whether sidebar is showing
    fullWindow?: boolean
    overrideSize?: Size
    expandedNav?: boolean
    expandedSearchMobile?: boolean
    composeFocused?: boolean
    quietFocus?: boolean
}

export const calcFullscreenSizeSub = (options?: UpdateFullscreenOptions) => {
    const {
        show, // sidebar
        fullWindow, // layout
        expandedNav, // layout
        expandedSearchMobile, // layout
        composeFocused, // portal
        quietFocus, // portal
        // overrideSize = { width: getWidth(), height: getHeight() }, // handleResize
    } = options || {}

    let changed = false
    // console.log('[calcFullscreenSizeSub]', options, lastCalcSizeState)
    if (show !== undefined && lastCalcSizeState.show !== show) {
        changed = true
        lastCalcSizeState.show = show
    }

    // any of the shared values (layout ones particularly) will end up triggering an early bail because they are present but haven't changed
    // -- check them all and track if any have changed and return then instead of potentially after each check

    if (fullWindow !== undefined) {
        if (lastCalcSizeState.fullWindow !== fullWindow) changed = true
        lastCalcSizeState.fullWindow = fullWindow
    }
    if (
        expandedNav !== undefined &&
        lastCalcSizeState.expandedNav !== expandedNav
    ) {
        changed = true
        lastCalcSizeState.expandedNav = expandedNav
    }
    if (
        expandedSearchMobile !== undefined &&
        lastCalcSizeState.expandedSearchMobile !== expandedSearchMobile
    ) {
        changed = true
        lastCalcSizeState.expandedSearchMobile = expandedSearchMobile
    }
    if (
        composeFocused !== undefined &&
        lastCalcSizeState.composeFocused !== composeFocused
    ) {
        changed = true
        lastCalcSizeState.composeFocused = composeFocused
    }
    if (
        quietFocus !== undefined &&
        lastCalcSizeState.quietFocus !== quietFocus
    ) {
        changed = true
        lastCalcSizeState.quietFocus = quietFocus
    }

    if (changed) {
        // updateFullscreenSize()
        setScreenOrientation({ overrideSkip: true })
    }
}

/// Floating event subs
pubSubLayout.on(LayoutEvent.PORTAL_CHANGE, calcFullscreenSizeSub)

export const handleViewportChange = () => {
    const width = getWidth()
    const height = getHeight()
    const bottomInset = getBrowserBottomInset()
    if (
        width === lastViewport.width &&
        height === lastViewport.height &&
        bottomInset === lastViewport.bottomInset
    )
        return
    Object.assign(lastViewport, { width, height, bottomInset })
    resolveFullWindow(true, height > offsetContentSize(width, height))
    debouncedFloaterCheck()
}

if (typeof window !== 'undefined') {
    const eventTarget = isScreenOrientationSupported
        ? window.screen.orientation
        : window
    const eventName = isScreenOrientationSupported
        ? 'change'
        : 'orientationchange'

    window.addEventListener('resize', handleViewportChange, {
        passive: true,
    })
    eventTarget.addEventListener(eventName, handleViewportChange, {
        passive: true,
    })

    installIOSPWAScrollRecovery()
}
